import { useTranslation } from '@rhino-project/core/i18n';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { FieldInput, FieldInputProps } from './FieldInput';
import { FieldTextarea, FieldTextareaProps } from './FieldTextarea';
import { parseDate } from '@internationalized/date';
import { FieldDatePicker, FieldDatePickerProps } from './FieldDatePicker';
import { FieldTimeInput, FieldTimeInputProps } from './FieldTimeInput';
import {
  Button,
  Checkbox,
  CheckboxContent,
  CheckboxControl,
  CheckboxIndicator,
  CheckboxProps,
  FieldError,
  InputGroup,
  InputGroupInput,
  InputGroupPrefix,
  Label,
  ListBoxItem
} from '@heroui/react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext
} from 'react-hook-form';
import { mergeRefs } from '@react-aria/utils';
import { stripRhinoProps } from './utils/stripRhinoProps';
import { Uploader } from '@rhino-project/core/utils';
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput
} from 'react-international-phone';
import { Icon, IconProps } from '@iconify/react';
import { FieldNumberInput, FieldNumberInputProps } from './FieldNumberInput';
import { FieldSelect, FieldSelectProps } from './FieldSelect';

// Types
export type FieldBooleanProps = CheckboxProps & {
  /**
   * The function to format the value before displaying it.
   */
  accessor?: (value: unknown) => boolean | string | null | undefined;
  /**
   * The path inside the form object.
   */
  path: string;
};
export type FieldBooleanIconProps = Omit<IconProps, 'icon'> & {
  /**
   * Icon to display when the value is true.
   */
  trueIcon?: string;
  /**
   * Icon to display when the value is true.
   */
  falseIcon?: string;
  /**
   * The path inside the form object.
   */
  path: string;
};
export type FieldCountryProps = FieldSelectProps;
export type FieldCurrencyProps = FieldNumberInputProps;
export type FieldDateProps = FieldDatePickerProps;
export type FieldDateTimeProps = FieldDatePickerProps;
export type FieldFileProps<T extends FieldValues = FieldValues> = {
  path: Path<T>;
  label: ReactNode;
  description: ReactNode;
  multiple?: boolean;
  accept?: string;
  maxSize?: number;
  maxFiles?: number;
};
export type FieldFloatProps = FieldNumberInputProps;
export type FieldHiddenProps = FieldInputProps;
export type FieldIntegerProps = FieldNumberInputProps;
export type FieldPasswordProps = FieldInputProps;
export type FieldPhoneProps = FieldInputProps;
export type FieldStringProps = FieldInputProps;
export type FieldTextProps = FieldTextareaProps;
export type FieldTimeProps = FieldTimeInputProps;
export type FieldYearProps = FieldIntegerProps;

// Boolean
export const FieldBooleanBase = ({
  accessor,
  path,
  ref,
  ...props
}: FieldBooleanProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const {
    field: { disabled, value: fieldValue, ref: fieldRef, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path
  });

  const mergedRef = useMemo(() => mergeRefs(fieldRef, ref), [fieldRef, ref]);

  const value = useMemo(() => {
    const accessedValue = accessor ? accessor(fieldValue) : fieldValue;

    if (typeof accessedValue === 'boolean') return accessedValue;
    if (typeof accessedValue !== 'string') return null;

    const normalized = accessedValue.trim().replace(/ /g, '').toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;

    return null;
  }, [accessor, fieldValue]);

  const { children, ...restProps } = props;

  return (
    <Checkbox
      // FIXME is this right for editing? maybe only if isClearable
      isIndeterminate={value !== true && value !== false}
      isSelected={value || false}
      isInvalid={!!error}
      isDisabled={disabled}
      {...fieldProps}
      {...restProps}
      ref={mergedRef}
    >
      <CheckboxContent>
        <CheckboxControl>
          <CheckboxIndicator />
        </CheckboxControl>
        {children as React.ReactNode}
      </CheckboxContent>
    </Checkbox>
  );
};

// Boolean Icon
export const FieldBooleanIconBase = ({
  trueIcon = 'bi:check',
  falseIcon = 'bi:x',
  path,
  ref,
  ...props
}: FieldBooleanIconProps & { ref?: React.Ref<SVGSVGElement> }) => {
  const {
    field: { onChange, value, ref: fieldRef, ...fieldProps }
  } = useController({
    name: path
  });

  const mergedRef = useMemo(() => mergeRefs(fieldRef, ref), [fieldRef, ref]);

  return (
    <Icon
      icon={value ? trueIcon : falseIcon}
      onClick={() => onChange(!value)}
      {...fieldProps}
      {...props}
      ref={mergedRef}
    />
  );
};

// Country
export const FieldCountryBase = ({
  ref,
  ...props
}: FieldCountryProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <FieldSelect ref={ref} {...props}>
      {defaultCountries.map((c) => {
        const country = parseCountry(c);
        return (
          <ListBoxItem
            key={country.iso2}
            id={country.iso2}
            textValue={country.name}
          >
            <FlagImage className="size-6" iso2={country.iso2} />
            {country.name}
          </ListBoxItem>
        );
      })}
    </FieldSelect>
  );
};

// Currency
export const FieldCurrencyBase = ({
  ref,
  ...props
}: FieldCurrencyProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldNumberInput
    ref={ref}
    formatOptions={{
      style: 'currency',
      currency: 'USD'
    }}
    {...props}
  />
);

// Date
export const FieldDateBase = ({
  ref,
  ...props
}: FieldDateProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldDatePicker ref={ref} granularity="day" parse={parseDate} {...props} />
);

// Date Time
export const FieldDateTimeBase = ({
  ref,
  ...props
}: FieldDateTimeProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldDatePicker ref={ref} {...props} />
);

// File
const fileMatchesAccept = (file: File, accept: string) => {
  if (!accept || accept === '*/*') return true;

  return accept
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .some((token) => {
      if (token.startsWith('.')) {
        return file.name.toLowerCase().endsWith(token.toLowerCase());
      }
      if (token.endsWith('/*')) {
        return file.type.startsWith(token.slice(0, -1));
      }
      return file.type === token;
    });
};

const IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'avif',
  'bmp',
  'svg'
];

// A stored attachment exposes a `url` and a `display_name` but not a content
// type, so fall back to the extension to decide whether it can be previewed.
const isImageFile = (file: {
  display_name?: string;
  content_type?: string;
}) => {
  if (file.content_type?.startsWith('image/')) return true;
  const ext = file.display_name?.split('.').pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.includes(ext);
};

const FieldFileBase = <T extends FieldValues = FieldValues>({
  path,
  label,
  description,
  multiple,
  accept = '*/*',
  maxSize = 5 * 1024 * 1024, // 5MB
  maxFiles = 5
}: FieldFileProps<T>) => {
  const { t } = useTranslation();
  const {
    field: { value, onChange, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: path
  });
  const { setError, clearErrors } = useFormContext();
  const [isDragOver, setIsDragOver] = useState(false);

  // Object URLs for freshly-picked images, keyed by signed_id, so a preview is
  // available before the attachment is persisted (a stored attachment carries
  // its own `url`). They must be revoked to avoid leaking blob references.
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>(
    {}
  );
  const localPreviewsRef = useRef<Record<string, string>>({});

  useEffect(
    () => () => {
      Object.values(localPreviewsRef.current).forEach((url) =>
        URL.revokeObjectURL(url)
      );
    },
    []
  );

  const validateFile = (file: File) => {
    if (file.size > maxSize) {
      return t('rhino.frontend.messages.files_must_be_smaller_than_valuemb', {
        value1: maxSize / 1024 / 1024
      });
    }

    return null;
  };

  const processFiles = (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;

    const accepted = selectedFiles.filter((file) =>
      fileMatchesAccept(file, accept)
    );
    if (accepted.length === 0) {
      setError(path, {
        type: 'custom',
        message: t('rhino.frontend.messages.file_type_not_accepted')
      });
      return;
    }

    if (multiple && accepted.length > maxFiles) {
      setError(path, {
        type: 'custom',
        message: t(
          'rhino.frontend.messages.you_can_only_upload_up_to_value_files',
          { value1: maxFiles }
        )
      });
      return;
    }

    const errors = accepted.map(validateFile).filter(Boolean);
    if (errors.length > 0) {
      setError(path, {
        type: 'custom',
        message: errors[0] || 'Invalid file'
      });
      return;
    }

    const uploaders = accepted.map((file, idx) => {
      const uploader = new Uploader({ id: `${path}-${idx}` }, file, () => null);

      return uploader.begin().then((arg) => ({
        file,
        result: {
          ...arg,
          display_name: arg?.filename
        } as { signed_id: string; display_name: string }
      }));
    });

    Promise.all(uploaders)
      .then((uploaded) => {
        clearErrors(path);
        const newFiles = uploaded.map((u) => u.result);

        // Build object-URL previews for any image just picked.
        const nextPreviews: Record<string, string> = {};
        uploaded.forEach(({ file, result }) => {
          if (file.type.startsWith('image/') && result.signed_id) {
            nextPreviews[result.signed_id] = URL.createObjectURL(file);
          }
        });
        if (Object.keys(nextPreviews).length > 0) {
          localPreviewsRef.current = {
            ...localPreviewsRef.current,
            ...nextPreviews
          };
          setLocalPreviews((prev) => ({ ...prev, ...nextPreviews }));
        }

        const nextValue = multiple
          ? [...(Array.isArray(value) ? value : []), ...newFiles]
          : newFiles[0];
        onChange(nextValue);
      })
      .catch((arg) =>
        setError(path, {
          type: 'custom',
          message: arg
        })
      );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;

    processFiles(Array.from(fileList));
  };

  // During dragover, inspect the DataTransfer items to see whether any of
  // them could be accepted. The browser only gives us MIME types here —
  // filename/extensions are withheld until drop for privacy — so we can
  // only filter by mime tokens. Extension-only accept patterns (like
  // `.pdf`) fall through and accept any file at hover time; they're
  // validated again on drop.
  const dragHasAcceptableItem = (
    event: React.DragEvent<HTMLDivElement>
  ): boolean => {
    if (!accept || accept === '*/*') return true;
    const items = Array.from(event.dataTransfer?.items || []);
    if (items.length === 0) return true;

    const mimeTokens = accept
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t && !t.startsWith('.'));
    if (mimeTokens.length === 0) return true;

    return items.some((item) => {
      if (item.kind !== 'file') return false;
      return mimeTokens.some((token) => {
        if (token.endsWith('/*'))
          return item.type.startsWith(token.slice(0, -1));
        return item.type === token;
      });
    });
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dragHasAcceptableItem(event)) {
      if (isDragOver) setIsDragOver(false);
      return;
    }
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList) return;

    processFiles(Array.from(fileList));
  };

  const revokeLocalPreview = (signedId: string) => {
    const url = localPreviewsRef.current[signedId];
    if (!url) return;
    URL.revokeObjectURL(url);
    delete localPreviewsRef.current[signedId];
    setLocalPreviews((prev) => {
      const next = { ...prev };
      delete next[signedId];
      return next;
    });
  };

  const handleRemoveFile = (fileToRemove: { signed_id: string }) => {
    if (multiple && Array.isArray(value)) {
      const newFiles = value.filter(
        (file: { signed_id: string }) =>
          file.signed_id !== fileToRemove.signed_id
      );
      onChange(newFiles);
    } else {
      onChange(null);
    }
    revokeLocalPreview(fileToRemove.signed_id);
    clearErrors(path);
  };

  const handleRemoveAllFiles = () => {
    onChange(multiple ? [] : null);
    Object.keys(localPreviewsRef.current).forEach(revokeLocalPreview);
    clearErrors(path);
  };

  const renderFileList = () => {
    const files =
      multiple && Array.isArray(value) ? value : value ? [value] : [];

    if (files.length === 0) return null;

    return (
      <div className="space-y-2">
        {multiple && files.length > 1 && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleRemoveAllFiles}
              className="text-sm text-red-600 hover:text-red-800"
            >
              {t('rhino.frontend.messages.remove_all')}
            </button>
          </div>
        )}
        {files.map(
          (
            file: {
              display_name: string;
              signed_id: string;
              url?: string;
              content_type?: string;
            },
            index
          ) => {
            const previewUrl =
              localPreviews[file.signed_id] ||
              (file.url && isImageFile(file) ? file.url : undefined);

            return (
              <div
                key={
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  `${file.display_name}-${index}`
                }
                className="p-4 bg-default/10 rounded-lg flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 truncate flex-1">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt={file.display_name}
                      className="h-12 w-12 rounded object-contain bg-default/40 shrink-0"
                    />
                  )}
                  <p className="text-sm font-medium text-muted truncate">
                    {file.display_name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(file)}
                  className="ml-4 text-muted hover:text-danger"
                >
                  <Icon className="w-5 h-5" icon="bi:x" />
                </button>
              </div>
            );
          }
        )}
      </div>
    );
  };

  const hasFiles =
    (multiple && Array.isArray(value) && value.length > 0) ||
    (!multiple && !!value);

  return (
    <div>
      <div
        className={`rounded-md border-2 border-dashed transition-colors ${
          isDragOver
            ? 'border-accent bg-accent/10'
            : error
              ? 'border-danger bg-danger/5'
              : 'border-default-300 bg-default hover:bg-default/70'
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <label className="flex flex-col items-center justify-center w-full p-4 gap-2 cursor-pointer">
          <div className="text-sm">{label}</div>
          <Icon className="w-8 h-8 text-muted" icon="bi:upload" />
          <p className="text-sm text-muted">
            <span className="font-semibold">
              {t('rhino.frontend.messages.click_to_upload')}
            </span>{' '}
            {t('rhino.frontend.messages.or_drag_and_drop')}
          </p>
          {description && <p className="text-xs text-muted">{description}</p>}
          <input
            {...fieldProps}
            type="file"
            className="hidden"
            accept={accept}
            multiple={multiple}
            onChange={handleFileChange}
          />
        </label>

        {hasFiles && <div className="px-4 pb-4">{renderFileList()}</div>}
      </div>

      {error && (
        <p className="text-xs text-danger px-1 mt-1 wrap-break-word">
          {error.message}
        </p>
      )}
    </div>
  );
};

// Float
export const FieldFloatBase = ({
  ref,
  ...props
}: FieldFloatProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldNumberInput ref={ref} {...props} />
);

// Hidden
export const FieldHiddenBase = ({
  ref,
  ...props
}: FieldInputProps & { ref?: React.Ref<HTMLInputElement> }) => (
  <FieldInput ref={ref} type="hidden" {...props} />
);

// Integer
export const FieldIntegerBase = ({
  ref,
  ...props
}: FieldIntegerProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldNumberInput
    ref={ref}
    formatOptions={{
      maximumFractionDigits: 0
    }}
    {...props}
  />
);

// Password
export const FieldPasswordBase = ({
  ref,
  ...props
}: FieldPasswordProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FieldInput
      type={showPassword ? 'text' : 'password'}
      endContent={
        <Button
          variant="ghost"
          size="sm"
          isIconOnly
          aria-label={
            showPassword
              ? t('rhino.frontend.messages.hide_password')
              : t('rhino.frontend.messages.show_password')
          }
          onPress={() => setShowPassword((show) => !show)}
        >
          <Icon icon={showPassword ? 'bi:eye-slash-fill' : 'bi:eye-fill'} />
        </Button>
      }
      ref={ref}
      {...props}
    />
  );
};

// Phone
export const FieldPhoneBase = ({
  ref,
  path,
  rules,
  shouldUnregister,
  defaultValue,
  disabled: propDisabled,
  label,
  placeholder,
  isRequired,
  className,
  ...props
}: FieldPhoneProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const {
    field: {
      disabled,
      value: fieldValue,
      onChange: fieldOnChange,
      ...fieldProps
    },
    fieldState: { error }
  } = useController({
    name: path,
    rules,
    shouldUnregister,
    defaultValue,
    disabled: propDisabled
  });

  const { country, handlePhoneValueChange, inputValue } = usePhoneInput({
    defaultCountry: 'us',
    value: (fieldValue as string) || '',
    countries: defaultCountries,
    onChange: ({ phone, country: currentCountry }) => {
      const dialCode = `+${currentCountry.dialCode}`;
      const next = phone === dialCode ? '' : phone;
      // Don't dirty the form on the mount/prefill no-op change. usePhoneInput
      // prefills the dial code for an empty value and fires onChange on mount;
      // since the edit controller uses keepDirtyValues, a spurious onChange('')
      // here would discard the record's real phone value once it loads.
      if (next !== ((fieldValue as string) || '')) {
        fieldOnChange(next);
      }
    }
  });

  return (
    <div
      className={`textfield${className ? ` ${className}` : ''}`}
      data-slot="textfield"
      data-invalid={!!error || undefined}
      data-disabled={disabled || undefined}
      data-required={isRequired || undefined}
    >
      {label && <Label>{label}</Label>}
      <InputGroup {...stripRhinoProps(props)}>
        <InputGroupPrefix>
          <FlagImage className="size-5" iso2={country.iso2} />
        </InputGroupPrefix>
        <InputGroupInput
          {...fieldProps}
          ref={ref}
          autoComplete="off"
          placeholder={placeholder}
          type="tel"
          disabled={disabled}
          required={isRequired}
          value={inputValue}
          onChange={handlePhoneValueChange}
        />
      </InputGroup>
      <FieldError
        data-visible={!!error || undefined}
        data-invalid={!!error || undefined}
      >
        {error?.message}
      </FieldError>
    </div>
  );
};

// String
export const FieldStringBase = ({
  ref,
  ...props
}: FieldStringProps & { ref?: React.Ref<HTMLInputElement> }) => (
  <FieldInput ref={ref} {...props} />
);

// Text
export const FieldTextBase = ({
  ref,
  ...props
}: FieldTextProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldTextarea ref={ref} {...props} />
);

// Time
export const FieldTimeBase = ({
  ref,
  ...props
}: FieldTimeProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldTimeInput ref={ref} {...props} />
);

// Year
export const FieldYearBase = ({
  ref,
  ...props
}: FieldYearProps & { ref?: React.Ref<HTMLDivElement> }) => (
  <FieldNumberInput
    ref={ref}
    formatOptions={{
      maximumFractionDigits: 0,
      useGrouping: false
    }}
    {...props}
  />
);

// Overrideable component exports
export const FieldBoolean = (props: FieldBooleanProps) =>
  useGlobalComponent('FieldBoolean', FieldBooleanBase, props);

export const FieldBooleanIcon = (props: FieldBooleanIconProps) =>
  useGlobalComponent('FieldBooleanIcon', FieldBooleanIconBase, props);

export const FieldCountry = (props: FieldCountryProps) =>
  useGlobalComponent('FieldCountry', FieldCountryBase, props);

export const FieldCurrency = (props: FieldCurrencyProps) =>
  useGlobalComponent('FieldCurrency', FieldCurrencyBase, props);

export const FieldDate = (props: FieldDateProps) =>
  useGlobalComponent('FieldDate', FieldDateBase, props);

export const FieldDateTime = (props: FieldDateTimeProps) =>
  useGlobalComponent('FieldDateTime', FieldDateTimeBase, props);

export const FieldFile = (props: FieldFileProps) =>
  useGlobalComponent('FieldFile', FieldFileBase, props);

export const FieldFloat = (props: FieldFloatProps) =>
  useGlobalComponent('FieldFloat', FieldFloatBase, props);

export const FieldHidden = (props: FieldInputProps) =>
  useGlobalComponent('FieldHidden', FieldHiddenBase, props);

export const FieldInteger = (props: FieldIntegerProps) =>
  useGlobalComponent('FieldInteger', FieldIntegerBase, props);

export const FieldPassword = (props: FieldPasswordProps) =>
  useGlobalComponent('FieldPassword', FieldPasswordBase, props);

export const FieldPhone = (props: FieldPhoneProps) =>
  useGlobalComponent('FieldPhone', FieldPhoneBase, props);

export const FieldString = (props: FieldStringProps) =>
  useGlobalComponent('FieldString', FieldStringBase, props);

export const FieldText = (props: FieldTextProps) =>
  useGlobalComponent('FieldText', FieldTextBase, props);

export const FieldTime = (props: FieldTimeProps) =>
  useGlobalComponent('FieldTime', FieldTimeBase, props);

export const FieldYear = (props: FieldYearProps) =>
  useGlobalComponent('FieldYear', FieldYearBase, props);
